/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, { IActionContext } from '@msdyn365-commerce/core';
import { action, computed, observable } from 'mobx';

import { IDistributorInfo, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    IListMapViewState,
    ILoadMapApiInput,
    IDistributorSelectionStateContext,
    IDistributorLocation,
    IDistributorSelectorStateManager
} from './i-distributor-selection-state-manager';

import { IMapWindow, MapLocationsResult } from './models/map-data';

declare const window: IMapWindow;

/**
 * Instance of IDistributorSelectorStateManager manager that uses Map service
 * for geo location.
 */
export class MapDistributorSelectorStateManager implements IDistributorSelectorStateManager {
    @observable public isMapApiLoaded?: boolean;
    @observable public selectedDistributorId?: number;
    @observable public hideDistributorWarning?: boolean;
    @observable public listMapViewState: IListMapViewState = { displayList: true, displayMap: false };
    @observable public isMapModuleLoaded?: boolean;
    @observable public showDistributorDetailsOnMap?: boolean;
    @observable public distributorList?: IDistributorInfo[];
    @observable protected _distributorDetailContext?: IDistributorSelectionStateContext;
    @observable protected _distributorSelectorContext?: IDistributorSelectionStateContext;

    private readonly SERVICE_ENDPOINT: string = 'https://dev.virtualearth.net/REST/v1/';
    private readonly MAP_API_ENDPOINT: string = 'https://www.bing.com/api/maps/mapcontrol?callback=mapAPIReady';

    @computed public get isDistributorSelectorDialogOpen(): Readonly<boolean> {
        return this._distributorSelectorContext !== undefined;
    }

    @computed public get isDistributorDetailPopupOpen(): Readonly<boolean> {
        return this._distributorDetailContext !== undefined;
    }

    @computed public get distributorSelectorContext(): Readonly<IDistributorSelectionStateContext | undefined> {
        return this._distributorSelectorContext;
    }

    private readonly hideDistributorWarningLocalStorageKey: string = '_msdyn365__hideDistributorWarning_';

    constructor() {
        this.isMapModuleLoaded = false;
        this.showDistributorDetailsOnMap = true;

        const hideDistributorWarning = this._readFromToCookie(this.hideDistributorWarningLocalStorageKey);
        try {
            const distributorWarning = hideDistributorWarning ? JSON.parse(hideDistributorWarning) : false;

            if (distributorWarning) {
                this.hideDistributorWarning = distributorWarning;
            }
        } catch {
            this.hideDistributorWarning = false;
        }
    }

    @action
    public async toggleListMapViewState(): Promise<void> {
        this.listMapViewState = {
            displayList: !this.listMapViewState.displayList,
            displayMap: !this.listMapViewState.displayMap
        };
    }

    @action
    public async setMapModuleLoaded(input: boolean): Promise<void> {
        this.isMapModuleLoaded = input;
    }

    @action
    public async setDistributorsList(input: IDistributorInfo[]): Promise<void> {
        this.distributorList = input;
    }

    @action
    public async setSelectedDistributorId(distributorId: number | undefined): Promise<void> {
        this.selectedDistributorId = distributorId;
    }

    @action
    public async hideWarningMessage(writeToCookie: boolean = false) {
        this.hideDistributorWarning = true;
        if (writeToCookie) {
            this._writeToCookie(this.hideDistributorWarningLocalStorageKey, JSON.stringify(true));
        }
    }

    @action
    public async updateDistributorMapVisibility(input: boolean) {
        this.showDistributorDetailsOnMap = input;
    }

    @action
    public async openDistributorSelectorDialog(input: IDistributorSelectionStateContext): Promise<void> {
        if (this._distributorSelectorContext) {
            throw new Error('Distributor selector dialog is already opened');
        }

        this._distributorSelectorContext = input;
    }

    @action
    public async openDistributorDetailPopup(input: IDistributorSelectionStateContext): Promise<void> {
        if (this._distributorDetailContext) {
            throw new Error('Distributor detail popup is already opened');
        }

        this._distributorDetailContext = input;
    }

    @action
    public async closeDistributorSelectorDialog(): Promise<void> {
        this._distributorSelectorContext = undefined;
    }

    @action
    public async closeDistributorDetailPopup(): Promise<void> {
        this._distributorDetailContext = undefined;
    }

    @action
    public async geoLocate(searchTerm: string, actionContext: IActionContext): Promise<IDistributorLocation | undefined> {
        if (
            !searchTerm ||
            !actionContext?.requestContext?.channel?.BingMapsApiKey ||
            !actionContext?.requestContext?.channel?.BingMapsEnabled
        ) {
            return undefined;
        }

        let searchContent: string = searchTerm;

        const jsonp = `mapSearchCallback_${crypto.getRandomValues(new Uint32Array(1))[0]}`;

        if (actionContext.requestContext.channel.ChannelCountryRegionISOCode) {
            searchContent = `${searchTerm.trim()} ${actionContext.requestContext.channel.ChannelCountryRegionISOCode}`;
        }

        const query = encodeURI(searchContent);
        const key = encodeURI(actionContext.requestContext.channel.BingMapsApiKey);

        const geolocationApiUrl =
            actionContext.requestContext.app &&
            actionContext.requestContext.app.config &&
            actionContext.requestContext.app.config.geolocationApiUrl;
        const requestUrl = `${
            geolocationApiUrl ? geolocationApiUrl : this.SERVICE_ENDPOINT
        }/Locations?query=${query}&key=${key}&jsonp=${jsonp}`;

        return new Promise<IDistributorLocation | undefined>(resolve => {
            const script = document.createElement('script');
            script.setAttribute('type', 'text/javascript');
            script.setAttribute('src', requestUrl);

            window[jsonp] = (data: MapLocationsResult) => {
                delete window[jsonp];
                script.remove();

                if (data && data.resourceSets && data.resourceSets.length > 0 && data.resourceSets[0].resources.length > 0) {
                    resolve({
                        latitude: data.resourceSets[0].resources[0].point.coordinates[0],
                        longitude: data.resourceSets[0].resources[0].point.coordinates[1]
                    });
                }

                resolve(undefined);
            };

            document.body.appendChild(script);
        });
    }

    @action
    public async loadMapApi(input: ILoadMapApiInput): Promise<void> {
        if (StringExtensions.isNullOrEmpty(input.key)) {
            return Promise.resolve();
        }

        const isScriptAlreadyLoaded = !!document?.getElementById('msdyn_map-api-script');

        if (isScriptAlreadyLoaded) {
            if (window) {
                const currentWindowAPI = window.mapAPIReady;
                window.mapAPIReady = () => {
                    if (currentWindowAPI) {
                        currentWindowAPI();
                    }
                    this.isMapApiLoaded = true;
                };
            }
            // Prevents from loading multiple script into the body
            return Promise.resolve();
        }

        const url = `${this.MAP_API_ENDPOINT}${input.key ? `&key=${input.key}` : ''}${input.lang ? `&setLang=${input.lang}` : ''}${
            input.market ? `&setMkt=${input.market}` : ''
        }`;

        return new Promise((resolve, reject) => {
            const script = document?.createElement('script');
            script.type = 'text/javascript';
            script.async = true;
            script.defer = true;
            script.src = url;
            script.id = 'msdyn_map-api-script';
            if (window) {
                window.mapAPIReady = () => {
                    this.isMapApiLoaded = true;
                    resolve();
                };
            }
            script.addEventListener('error', (error: string | Event) => {
                reject(error);
            });
            document?.body?.appendChild(script);
        });
    }

    private _writeToCookie(key: string, value: string | undefined): void {
        if (!key || !value) {
            return;
        }
        if (MsDyn365.isBrowser) {
            const storage = window.localStorage;
            storage.setItem(key, value);
        }
    }

    private _readFromToCookie(key: string): string | undefined {
        if (!key) {
            return undefined;
        }
        if (MsDyn365.isBrowser) {
            const storage = window.localStorage;
            const cookieValue = storage.getItem(key);
            if (cookieValue) {
                return cookieValue;
            }
        }

        return undefined;
    }
}
